import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatDateAU } from '../../../shared/format';
import {
  ENTITY_KINDS, ENTITY_KIND_LABEL, ENTITY_KIND_SHORT, ENTITY_ROLE_LABEL, ROLES_FOR_KIND, isValidAbn, isValidAcn, personLabel,
  type EntityKind, type EntityPerson, type EntityReviewItem, type EntityRole,
} from '../../../shared/entities';
import { QAEmpty } from './QAEmpty';
import { QASkeleton } from './QASkeleton';

interface LinkedAccount { id: string; name: string; type: string; value: number }
interface EntityRow {
  id: string; clientId: string; kind: EntityKind; name: string; abn: string | null; acn: string | null;
  establishedDate: string | null; trusteeKind: 'individual' | 'corporate' | null; trusteeCompany: string | null;
  deedDate: string | null; deedReviewedDate: string | null; investmentStrategyReviewedDate: string | null;
  lastAuditDate: string | null; auditorName: string | null; auditorFirm: string | null;
  people: EntityPerson[]; notes: string | null;
  accounts: LinkedAccount[]; reviews: EntityReviewItem[];
}
interface EntitiesResponse { data: EntityRow[]; unlinkedAccounts: LinkedAccount[] }
interface Member { id: string; firstName: string | null; surname: string | null; role: string }

const fmtAbn = (v: string | null) => (v && /^\d{11}$/.test(v) ? `${v.slice(0, 2)} ${v.slice(2, 5)} ${v.slice(5, 8)} ${v.slice(8)}` : v ?? '');
const fmtAcn = (v: string | null) => (v && /^\d{9}$/.test(v) ? `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6)}` : v ?? '');

export function EntitiesTab({ clientId, members, canEdit }: { clientId: string; members: Member[]; canEdit: boolean }) {
  const qc = useQueryClient();
  const key = ['client', clientId, 'entities'];
  const q = useQuery({ queryKey: key, queryFn: () => api<EntitiesResponse>(`/api/clients/${clientId}/entities`) });
  const [editing, setEditing] = useState<EntityRow | 'new' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: key }),
    qc.invalidateQueries({ queryKey: ['client', clientId, 'full'] }),
  ]);

  async function link(accountId: string, entityId: string | null) {
    try {
      await api(`/api/clients/${clientId}/accounts/${accountId}/entity`, { method: 'PATCH', body: JSON.stringify({ entityId }) });
      await refresh();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not link the account'); }
  }

  if (q.isLoading) return <QASkeleton variant="detail" />;
  if (q.isError || !q.data) return <div className="qa-badge qa-badge--out">Unable to load entities</div>;
  const rows = q.data.data;
  const unlinked = q.data.unlinkedAccounts;

  return (
    <div className="space-y-6" data-testid="entities-tab">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="qa-eyebrow">Entities</p>
          <p className="text-sm text-ink-mute">SMSFs, trusts and companies in this household, the people in them, and the accounts they own.</p>
        </div>
        {canEdit && !editing && <button type="button" className="btn-primary text-sm" onClick={() => setEditing('new')}>Add entity</button>}
      </div>
      {msg && <p role="status" className="text-sm text-rust-deep">{msg}</p>}

      {editing && (
        <EntityForm
          clientId={clientId} members={members} initial={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async (text) => { setEditing(null); setMsg(text); await refresh(); window.setTimeout(() => setMsg(null), 4000); }}
        />
      )}

      {rows.length === 0 && !editing && <QAEmpty inline title="No entities recorded." />}

      {rows.filter((e) => editing === 'new' || !editing || editing.id !== e.id).map((e) => (
        <EntityCard key={e.id} e={e} members={members} canEdit={canEdit}
          onEdit={() => setEditing(e)} onUnlink={(aid) => link(aid, null)} />
      ))}

      {unlinked.length > 0 && (
        <section className="qa-card p-5" aria-label="Accounts held personally">
          <p className="qa-eyebrow mb-3">Accounts held personally</p>
          <ul className="divide-y divide-hairline-paper">
            {unlinked.map((a) => (
              <li key={a.id} className="py-2 flex flex-wrap items-center justify-between gap-3 text-sm">
                <span>{a.name} <span className="text-ink-mute text-xs uppercase tracking-widest ml-1">{a.type}</span></span>
                <span className="flex items-center gap-3">
                  <span className="qa-mono">{formatAUD(a.value)}</span>
                  {canEdit && rows.length > 0 && (
                    <select className="qa-select text-xs" aria-label={`Owner of ${a.name}`} value="" onChange={(ev) => ev.target.value && link(a.id, ev.target.value)}>
                      <option value="">Owned by…</option>
                      {rows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function reviewTone(state: EntityReviewItem['state']): string {
  return state === 'overdue' ? 'qa-badge--out' : state === 'dueSoon' ? 'qa-badge--hold' : 'qa-badge--mute';
}

function EntityCard({ e, members, canEdit, onEdit, onUnlink }: {
  e: EntityRow; members: Member[]; canEdit: boolean; onEdit: () => void; onUnlink: (accountId: string) => void;
}) {
  const total = e.accounts.reduce((s, a) => s + a.value, 0);
  const facts: Array<[string, string | null]> = [
    ['ABN', e.abn ? fmtAbn(e.abn) : null],
    ['ACN', e.acn ? fmtAcn(e.acn) : null],
    ['Established', e.establishedDate ? formatDateAU(e.establishedDate) : null],
    ['Trustee', e.kind === 'company' ? null : e.trusteeKind === 'corporate' ? (e.trusteeCompany || 'Corporate trustee') : e.trusteeKind === 'individual' ? 'Individual trustees' : null],
    ['Deed', e.deedDate ? formatDateAU(e.deedDate) : null],
    ['Deed reviewed', e.deedReviewedDate ? formatDateAU(e.deedReviewedDate) : null],
    ['Strategy reviewed', e.kind === 'smsf' && e.investmentStrategyReviewedDate ? formatDateAU(e.investmentStrategyReviewedDate) : null],
    ['Last audit', e.kind === 'smsf' && e.lastAuditDate ? formatDateAU(e.lastAuditDate) : null],
    ['Auditor', e.kind === 'smsf' ? [e.auditorName, e.auditorFirm].filter(Boolean).join(', ') || null : null],
  ];
  return (
    <section className="qa-card" aria-label={e.name} data-testid="entity-card">
      <header className="px-5 py-4 flex flex-wrap items-start justify-between gap-4 border-b border-hairline-paper">
        <div>
          <p className="qa-mono text-[10px] uppercase tracking-widest text-ink-mute">{ENTITY_KIND_LABEL[e.kind]}</p>
          <h3 className="qa-display text-lg">{e.name}</h3>
          {e.reviews.length > 0 && (
            <ul className="flex flex-wrap gap-2 mt-2" aria-label="Reviews">
              {e.reviews.map((r) => <li key={r.kind} className={`qa-badge ${reviewTone(r.state)}`}>{r.detail}</li>)}
            </ul>
          )}
        </div>
        <div className="flex items-start gap-6">
          <div className="text-right">
            <p className="qa-stat__label">Accounts</p>
            <p className="qa-mono text-base">{formatAUD(total)}</p>
            <p className="text-[11px] text-ink-mute">{e.accounts.length} account{e.accounts.length === 1 ? '' : 's'}</p>
          </div>
          {canEdit && <button type="button" className="btn-ghost text-sm" onClick={onEdit}>Edit</button>}
        </div>
      </header>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-5 py-4">
        <dl className="text-sm space-y-1">
          {facts.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3"><dt className="text-ink-mute">{k}</dt><dd className="text-right">{v}</dd></div>
          ))}
          {facts.every(([, v]) => !v) && <p className="text-ink-mute">No details recorded.</p>}
        </dl>
        <div>
          <p className="qa-stat__label mb-2">People</p>
          {e.people.length === 0 ? <p className="text-sm text-ink-mute">None recorded.</p> : (
            <ul className="text-sm space-y-1">
              {e.people.map((p, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span>{personLabel(p, members)}</span>
                  <span className="text-ink-mute">{ENTITY_ROLE_LABEL[p.role]}{p.share != null ? ` · ${Math.round(p.share * 1000) / 10}%` : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="qa-stat__label mb-2">Accounts owned</p>
          {e.accounts.length === 0 ? <p className="text-sm text-ink-mute">None linked.</p> : (
            <ul className="text-sm space-y-1">
              {e.accounts.map((a) => (
                <li key={a.id} className="flex justify-between gap-3">
                  <span className="truncate">{a.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="qa-mono">{formatAUD(a.value)}</span>
                    {canEdit && <button type="button" className="text-[11px] text-ink-mute hover:text-rust" onClick={() => onUnlink(a.id)} aria-label={`Unlink ${a.name}`}>Unlink</button>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {e.notes && <p className="px-5 pb-4 text-sm text-ink-mute whitespace-pre-wrap">{e.notes}</p>}
    </section>
  );
}

/* ---------------- Form ---------------- */

interface Draft {
  kind: EntityKind; name: string; abn: string; acn: string; establishedDate: string;
  trusteeKind: '' | 'individual' | 'corporate'; trusteeCompany: string;
  deedDate: string; deedReviewedDate: string; investmentStrategyReviewedDate: string; lastAuditDate: string;
  auditorName: string; auditorFirm: string; notes: string;
  people: Array<{ role: EntityRole; who: string; name: string; share: string }>;
}

function toDraft(e: EntityRow | null): Draft {
  return {
    kind: e?.kind ?? 'smsf', name: e?.name ?? '', abn: fmtAbn(e?.abn ?? null), acn: fmtAcn(e?.acn ?? null),
    establishedDate: e?.establishedDate ?? '', trusteeKind: e?.trusteeKind ?? '', trusteeCompany: e?.trusteeCompany ?? '',
    deedDate: e?.deedDate ?? '', deedReviewedDate: e?.deedReviewedDate ?? '',
    investmentStrategyReviewedDate: e?.investmentStrategyReviewedDate ?? '', lastAuditDate: e?.lastAuditDate ?? '',
    auditorName: e?.auditorName ?? '', auditorFirm: e?.auditorFirm ?? '', notes: e?.notes ?? '',
    people: (e?.people ?? []).map((p) => ({ role: p.role, who: p.memberId ?? 'other', name: p.name ?? '', share: p.share != null ? String(Math.round(p.share * 1000) / 10) : '' })),
  };
}

function EntityForm({ clientId, members, initial, onCancel, onSaved }: {
  clientId: string; members: Member[]; initial: EntityRow | null; onCancel: () => void; onSaved: (msg: string) => void;
}) {
  const [d, setD] = useState<Draft>(() => toDraft(initial));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
  const adults = members.filter((m) => m.role !== 'dependant');
  const pickable = members;
  const roles = ROLES_FOR_KIND[d.kind];
  const abnBad = d.abn.trim() !== '' && !isValidAbn(d.abn);
  const acnBad = d.acn.trim() !== '' && !isValidAcn(d.acn);
  const showShare = d.kind === 'company' || d.kind === 'smsf';

  async function save() {
    if (!d.name.trim()) { setErr('Give the entity a name'); return; }
    if (abnBad) { setErr('The ABN does not pass the ATO check digit test'); return; }
    if (acnBad) { setErr('The ACN does not pass the ASIC check digit test'); return; }
    const people: EntityPerson[] = [];
    for (const p of d.people) {
      if (p.who === 'other' && !p.name.trim()) continue;
      const share = p.share.trim() === '' ? null : Number(p.share) / 100;
      if (share != null && (!Number.isFinite(share) || share < 0 || share > 1)) { setErr('Shares are percentages between 0 and 100'); return; }
      people.push({ role: p.role, memberId: p.who === 'other' ? null : p.who, name: p.who === 'other' ? p.name.trim() : null, share });
    }
    const smsf = d.kind === 'smsf';
    const trustLike = d.kind === 'smsf' || d.kind === 'trust';
    const body = {
      kind: d.kind, name: d.name.trim(), abn: d.abn.replace(/\s+/g, '') || null, acn: d.acn.replace(/\s+/g, '') || null,
      establishedDate: d.establishedDate || null,
      trusteeKind: trustLike ? (d.trusteeKind || null) : null,
      trusteeCompany: trustLike && d.trusteeKind === 'corporate' ? (d.trusteeCompany.trim() || null) : null,
      deedDate: trustLike ? (d.deedDate || null) : null,
      deedReviewedDate: trustLike ? (d.deedReviewedDate || null) : null,
      investmentStrategyReviewedDate: smsf ? (d.investmentStrategyReviewedDate || null) : null,
      lastAuditDate: smsf ? (d.lastAuditDate || null) : null,
      auditorName: smsf ? (d.auditorName.trim() || null) : null,
      auditorFirm: smsf ? (d.auditorFirm.trim() || null) : null,
      notes: d.notes.trim() || null,
      people,
    };
    setBusy(true); setErr(null);
    try {
      if (initial) await api(`/api/clients/${clientId}/entities/${initial.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      else await api(`/api/clients/${clientId}/entities`, { method: 'POST', body: JSON.stringify(body) });
      onSaved(initial ? `${body.name} updated` : `${body.name} added`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save'); } finally { setBusy(false); }
  }

  async function remove() {
    if (!initial) return;
    setBusy(true);
    try {
      await api(`/api/clients/${clientId}/entities/${initial.id}`, { method: 'DELETE' });
      onSaved(`${initial.name} removed; its accounts are now shown as held personally`);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not remove'); setBusy(false); }
  }

  const field = (label: string, el: ReactNode, hint?: string) => (
    <label className="text-xs block">
      <span className="text-ink-mute">{label}</span>
      <span className="block mt-1">{el}</span>
      {hint && <span className="block text-[11px] text-rust-deep mt-0.5">{hint}</span>}
    </label>
  );
  const date = (k: 'establishedDate' | 'deedDate' | 'deedReviewedDate' | 'investmentStrategyReviewedDate' | 'lastAuditDate', label: string) =>
    field(label, <input type="date" className="qa-input w-full text-sm" value={d[k]} onChange={(e) => set(k, e.target.value)} />);

  return (
    <section className="qa-card p-5 space-y-5" aria-label={initial ? `Edit ${initial.name}` : 'New entity'}>
      <p className="qa-eyebrow">{initial ? `Edit ${initial.name}` : 'New entity'}</p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {field('Type', (
          <select className="qa-select w-full text-sm" aria-label="Type" value={d.kind} onChange={(e) => set('kind', e.target.value as EntityKind)}>
            {ENTITY_KINDS.map((k) => <option key={k} value={k}>{ENTITY_KIND_LABEL[k]}</option>)}
          </select>
        ))}
        <div className="md:col-span-3">{field('Name', <input className="qa-input w-full text-sm" aria-label="Name" value={d.name} onChange={(e) => set('name', e.target.value)} placeholder={d.kind === 'smsf' ? 'e.g. Smith Family Super Fund' : d.kind === 'trust' ? 'e.g. Smith Family Trust' : 'e.g. Smith Holdings Pty Ltd'} />)}</div>
        {field('ABN', <input className="qa-input w-full text-sm qa-mono" aria-label="ABN" aria-invalid={abnBad} value={d.abn} onChange={(e) => set('abn', e.target.value)} inputMode="numeric" />, abnBad ? 'Check digit fails' : undefined)}
        {d.kind === 'company' && field('ACN', <input className="qa-input w-full text-sm qa-mono" aria-label="ACN" aria-invalid={acnBad} value={d.acn} onChange={(e) => set('acn', e.target.value)} inputMode="numeric" />, acnBad ? 'Check digit fails' : undefined)}
        {date('establishedDate', 'Established')}
        {(d.kind === 'smsf' || d.kind === 'trust') && field('Trustee', (
          <select className="qa-select w-full text-sm" aria-label="Trustee" value={d.trusteeKind} onChange={(e) => set('trusteeKind', e.target.value as Draft['trusteeKind'])}>
            <option value="">Not recorded</option>
            <option value="individual">Individual trustees</option>
            <option value="corporate">Corporate trustee</option>
          </select>
        ))}
        {(d.kind === 'smsf' || d.kind === 'trust') && d.trusteeKind === 'corporate' && field('Trustee company', <input className="qa-input w-full text-sm" value={d.trusteeCompany} onChange={(e) => set('trusteeCompany', e.target.value)} />)}
        {(d.kind === 'smsf' || d.kind === 'trust') && date('deedDate', 'Deed dated')}
        {(d.kind === 'smsf' || d.kind === 'trust') && date('deedReviewedDate', 'Deed last reviewed')}
        {d.kind === 'smsf' && date('investmentStrategyReviewedDate', 'Investment strategy reviewed')}
        {d.kind === 'smsf' && date('lastAuditDate', 'Last audit signed')}
        {d.kind === 'smsf' && field('Auditor', <input className="qa-input w-full text-sm" value={d.auditorName} onChange={(e) => set('auditorName', e.target.value)} />)}
        {d.kind === 'smsf' && field('Audit firm', <input className="qa-input w-full text-sm" value={d.auditorFirm} onChange={(e) => set('auditorFirm', e.target.value)} />)}
      </div>

      <div>
        <div className="flex items-baseline justify-between mb-2">
          <p className="qa-stat__label">People</p>
          <button type="button" className="btn-ghost text-xs" onClick={() => set('people', [...d.people, { role: roles[0], who: adults[0]?.id ?? 'other', name: '', share: '' }])}>Add person</button>
        </div>
        {d.people.length === 0 && <p className="text-sm text-ink-mute">No people recorded yet.</p>}
        <ul className="space-y-2">
          {d.people.map((p, i) => (
            <li key={i} className="qa-entity-person"
              style={{ ['--cols' as string]: ['9rem', 'minmax(0,1fr)', p.who === 'other' ? 'minmax(0,1fr)' : '', showShare ? '6rem' : '', 'auto'].filter(Boolean).join(' ') }}>
              <select className="qa-select text-sm" aria-label={`Role ${i + 1}`} value={p.role}
                onChange={(e) => set('people', d.people.map((x, j) => (j === i ? { ...x, role: e.target.value as EntityRole } : x)))}>
                {[...new Set([...roles, p.role])].map((r) => <option key={r} value={r}>{ENTITY_ROLE_LABEL[r]}</option>)}
              </select>
              <select className="qa-select text-sm" aria-label={`Person ${i + 1}`} value={p.who}
                onChange={(e) => set('people', d.people.map((x, j) => (j === i ? { ...x, who: e.target.value } : x)))}>
                {pickable.map((m) => <option key={m.id} value={m.id}>{[m.firstName, m.surname].filter(Boolean).join(' ') || 'Household member'}</option>)}
                <option value="other">Someone else…</option>
              </select>
              {p.who === 'other' && (
                <input className="qa-input text-sm" placeholder="Name" aria-label={`Name ${i + 1}`} value={p.name}
                  onChange={(e) => set('people', d.people.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              )}
              {showShare && (
                <input className="qa-input text-sm w-full qa-mono" placeholder="Share %" aria-label={`Share ${i + 1}`} inputMode="decimal" value={p.share}
                  onChange={(e) => set('people', d.people.map((x, j) => (j === i ? { ...x, share: e.target.value } : x)))} />
              )}
              <button type="button" className="text-xs text-ink-mute hover:text-rust" aria-label={`Remove person ${i + 1}`}
                onClick={() => set('people', d.people.filter((_, j) => j !== i))}>Remove</button>
            </li>
          ))}
        </ul>
      </div>

      {field('Notes', <textarea className="qa-textarea w-full text-sm min-h-[70px]" value={d.notes} onChange={(e) => set('notes', e.target.value)} />)}

      {err && <p role="alert" className="text-sm text-rust-deep">{err}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-primary text-sm" disabled={busy} onClick={save}>{initial ? 'Save changes' : 'Add entity'}</button>
        <button type="button" className="btn-ghost text-sm" disabled={busy} onClick={onCancel}>Cancel</button>
        {initial && (
          confirmDelete
            ? <span className="ml-auto flex items-center gap-2 text-sm"><span className="text-rust-deep">Remove {initial.name}?</span>
                <button type="button" className="btn-ghost text-sm text-rust-deep" disabled={busy} onClick={remove}>Yes, remove</button>
                <button type="button" className="btn-ghost text-sm" onClick={() => setConfirmDelete(false)}>Keep</button></span>
            : <button type="button" className="btn-ghost text-sm ml-auto" onClick={() => setConfirmDelete(true)}>Remove entity</button>
        )}
      </div>
      <p className="text-[11px] text-ink-mute">{ENTITY_KIND_SHORT[d.kind]} review windows are set in practice settings.</p>
    </section>
  );
}
